import React from 'react';
import { useStyletron } from 'baseui';
import { Button, KIND as ButtonKind, SHAPE, SIZE } from 'baseui/button';
import { HeadingSmall, LabelMedium } from 'baseui/typography';
import { Plus, Delete, ChevronRight, ChevronDown } from 'baseui/icon';
import { Modal, ModalHeader, ModalBody } from 'baseui/modal';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import useStore from '../store';
import { CustomSelect } from './CustomSelect';
import { MethodTag } from './MethodTag';

export function EndpointList() {
  const [css] = useStyletron();
  const store = useStore();
  const [expandedEndpoint, setExpandedEndpoint] = React.useState(null);
  const [isNewModalOpen, setIsNewModalOpen] = React.useState(false);

  const handleEditEndpoint = (endpoint) => {
    setExpandedEndpoint(expandedEndpoint === endpoint.id ? null : endpoint.id);
  };

  const handleDeleteEndpoint = async (endpoint) => {
    if (window.confirm(`Are you sure you want to delete endpoint "${endpoint.path}"?`)) {
      try {
        await store.deleteEndpoint(endpoint.id);
        setExpandedEndpoint(null);
      } catch (error) {
        console.error('Failed to delete endpoint:', error);
      }
    }
  };

  const handleNewEndpoint = () => {
    store.setSelectedEndpoint({});
    setIsNewModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      if (formData.id) {
        await store.updateEndpoint(formData.id, formData);
      } else {
        await store.createEndpoint(formData);
      }
      setExpandedEndpoint(null);
      setIsNewModalOpen(false);
    } catch (error) {
      console.error('Failed to save endpoint:', error);
    }
  };

  return (
    <div className={css({
      backgroundColor: '#fff',
      paddingTop: '24px',
      paddingRight: '24px',
      paddingBottom: '24px',
      paddingLeft: '24px',
      flex: 1,
      height: '100%'
    })}>
      <div className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        backgroundColor: '#fff',
        paddingTop: '16px',
        paddingRight: '24px',
        paddingBottom: '16px',
        paddingLeft: '24px',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
      })}>
        <HeadingSmall margin="0">Endpoints</HeadingSmall>
        <Button
          onClick={handleNewEndpoint}
          startEnhancer={<Plus />}
          size={SIZE.compact}
        >
          Add Endpoint
        </Button>
      </div>

      {store.error && (
        <div className={css({
          backgroundColor: '#FFEBEE',
          color: '#C62828',
          paddingTop: '16px',
          paddingRight: '24px',
          paddingBottom: '16px',
          paddingLeft: '24px',
          marginBottom: '16px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        })}>
          <LabelMedium>{store.error}</LabelMedium>
        </div>
      )}

      {store.loading ? (
        <div className={css({
          backgroundColor: '#fff',
          paddingTop: '16px',
          paddingRight: '24px',
          paddingBottom: '16px',
          paddingLeft: '24px',
          marginBottom: '16px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          textAlign: 'center'
        })}>
          <LabelMedium>Loading endpoints...</LabelMedium>
        </div>
      ) : !store.error && (store.endpoints || []).length === 0 ? (
        <div className={css({
          backgroundColor: '#fff',
          paddingTop: '16px',
          paddingRight: '24px',
          paddingBottom: '16px',
          paddingLeft: '24px',
          marginBottom: '16px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          textAlign: 'center'
        })}>
          <LabelMedium>No endpoints found. Click "Add Endpoint" to create one.</LabelMedium>
        </div>
      ) : (
        <div className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '1px',
          backgroundColor: '#F7F7F7'
        })}>
          {store.endpoints.map(endpoint => (
            <React.Fragment key={endpoint.id}>
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  paddingTop: '12px',
                  paddingRight: '16px',
                  paddingBottom: '12px',
                  paddingLeft: '16px',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: '#F8F9FA'
                  }
                })}
                onClick={() => handleEditEndpoint(endpoint)}
              >
                <div className={css({ 
                  display: 'flex',
                  alignItems: 'center'
                })}>
                  {expandedEndpoint === endpoint.id ? (
                    <ChevronDown size={20} />
                  ) : (
                    <ChevronRight size={20} />
                  )}
                </div>
                <MethodTag method={endpoint.method} />
                <div className={css({
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem'
                })}>
                  <LabelMedium>{endpoint.name}</LabelMedium>
                  <div className={css({
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    color: '#4A5568'
                  })}>
                    {endpoint.url}
                  </div>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteEndpoint(endpoint);
                  }}
                  kind={ButtonKind.tertiary}
                  size={SIZE.mini}
                  shape={SHAPE.circle}
                >
                  <Delete size={24} />
                </Button>
              </div>
              {expandedEndpoint === endpoint.id && (
                <EndpointForm
                  endpoint={endpoint}
                  onSubmit={handleSubmit}
                  onCancel={() => setExpandedEndpoint(null)}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <Modal
        onClose={() => setIsNewModalOpen(false)}
        isOpen={isNewModalOpen}
        animate
        autoFocus
        size="auto"
      >
        <ModalHeader>Create New Endpoint</ModalHeader>
        <ModalBody>
          <EndpointForm
            onSubmit={handleSubmit}
            onCancel={() => setIsNewModalOpen(false)}
          />
        </ModalBody>
      </Modal>
    </div>
  );
}

function EndpointForm({ endpoint, onSubmit, onCancel }) {
  const [css] = useStyletron();
  const store = useStore();
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    projectId: 'default',
    method: 'GET',
    path: '',
    ...endpoint
  });

  const servicesFetched = React.useRef(false);
  const operationsFetched = React.useRef({});
  const operationsFetchFailed = React.useRef(new Set());

  const fetchOperations = React.useCallback(async () => {
    if (formData.serviceId && 
        (!store.serviceOperations?.[formData.serviceId]?.length) && 
        !store.error &&
        !operationsFetched.current[formData.serviceId] &&
        !operationsFetchFailed.current.has(formData.serviceId)) {
      operationsFetched.current[formData.serviceId] = true;
      try {
        await store.fetchServiceOperations(formData.serviceId);
        operationsFetchFailed.current.delete(formData.serviceId);
      } catch (error) {
        operationsFetchFailed.current.add(formData.serviceId);
        console.error(`Failed to fetch operations for service ${formData.serviceId}:`, error);
        if (error.message === 'Service not found or has no operations') {
          setFormData(prev => ({ ...prev, serviceId: '', operationId: '' }));
        }
      }
    }
  }, [formData.serviceId, store]);

  const loadServices = React.useCallback(async () => {
    if (!store.services?.length && !servicesFetched.current && !store.error) {
      servicesFetched.current = true;
      try {
        await store.fetchServices();
      } catch (error) {
        console.error('Failed to fetch services:', error);
      }
    }
  }, [store]);

  React.useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  React.useEffect(() => {
    loadServices();
  }, [loadServices]);

  React.useEffect(() => {
    if (!formData.serviceId) {
      operationsFetchFailed.current.clear();
    }
  }, [formData.serviceId]);

  const getServiceOperations = (serviceId) => {
    if (!serviceId || !store.serviceOperations[serviceId]) return [];
    return store.serviceOperations[serviceId];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.serviceId || !formData.operationId || !formData.method || !formData.path) {
      alert('Please fill in all required fields');
      return;
    }
    await onSubmit(formData);
  };

  return (
    <form 
      onSubmit={handleSubmit}
      className={css({
        backgroundColor: '#F8F9FA',
        paddingTop: '16px',
        paddingRight: '16px',
        paddingBottom: '16px',
        paddingLeft: '16px',
        borderTop: '1px solid #E2E8F0'
      })}
    >
      <div className={css({
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem',
        marginBottom: '1rem'
      })}>
        <FormControl label="Name *" caption="A descriptive name for the endpoint">
          <Input
            value={formData.name || ''}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder="My API Endpoint"
          />
        </FormControl>

        <FormControl label="Description" caption="Optional description of the endpoint's purpose">
          <Input
            value={formData.description || ''}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="This endpoint is used for..."
          />
        </FormControl>

        <FormControl label="Service *" caption="Select the associated service">
          <CustomSelect
            options={store.services.map(service => ({
              label: service.name,
              id: service.id
            }))}
            value={formData.serviceId ? [{
              label: store.services.find(s => s.id === formData.serviceId)?.name,
              id: formData.serviceId
            }] : []}
            onChange={async ({ value }) => {
              const serviceId = value[0]?.id;
              if (serviceId) {
                await store.fetchServiceOperations(serviceId);
              }
              setFormData({ ...formData, serviceId, operationId: null });
            }}
            placeholder="Select service"
          />
        </FormControl>

        {formData.serviceId && (
          <FormControl label="Operation *" caption="Select the associated operation">
            <CustomSelect
              options={getServiceOperations(formData.serviceId).map(op => ({
                label: `${op.method} ${op.path}`,
                id: op.id
              }))}
              value={formData.operationId ? [{
                label: getServiceOperations(formData.serviceId).find(op => op.id === formData.operationId)?.path,
                id: formData.operationId
              }] : []}
              onChange={({ value }) => setFormData({ ...formData, operationId: value[0]?.id })}
              placeholder="Select operation"
            />
          </FormControl>
        )}

        <FormControl label="Method *" caption="HTTP method for this endpoint">
          <CustomSelect
            options={[
              { label: 'GET', id: 'GET' },
              { label: 'POST', id: 'POST' },
              { label: 'PUT', id: 'PUT' },
              { label: 'DELETE', id: 'DELETE' },
              { label: 'PATCH', id: 'PATCH' },
            ]}
            value={formData.method ? [{ label: formData.method, id: formData.method }] : []}
            onChange={({ value }) => setFormData({ ...formData, method: value[0]?.id })}
            placeholder="Select method"
          />
        </FormControl>

        <FormControl label="Path *" caption="URL path for this endpoint">
          <Input
            value={formData.path || ''}
            onChange={e => setFormData({ ...formData, path: e.target.value })}
            placeholder="/api/resource/{id}"
          />
        </FormControl>
      </div>

      <div className={css({
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.5rem'
      })}>
        <Button kind="tertiary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {endpoint ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
